import type { Condition, Definition } from '@survey/core';
import { Filter, Plus, Scale, X } from 'lucide-react';
import { useState } from 'react';
import type { ResponseFilter } from '../../api/client';
import { useI18n } from '../../i18n';
import { ConditionRow, conditionSources, defaultValue, operatorsFor } from '../builder/LogicEditor';
import { Switch } from '../ui';

interface Props {
  def: Definition;
  filter: ResponseFilter;
  onChange: (f: ResponseFilter) => void;
  /** Weighting is configured, so it can be switched on. */
  canWeight: boolean;
  testTotal: number;
}

/** Narrows the dashboard, cross-tabs and exports to a subset of responses. */
export function FilterBar({ def, filter, onChange, canWeight, testTotal }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(!!filter.conditions?.length);
  const sources = conditionSources(def, def.questions);
  const conditions = filter.conditions ?? [];
  const set = (p: Partial<ResponseFilter>) => onChange({ ...filter, ...p });
  const setConditions = (next: Condition[]) => set({ conditions: next.length ? next : undefined });
  const active = !!(filter.from || filter.to || filter.source || filter.approval || conditions.length);
  return (
    <div className="card filter-bar mb-16 no-print">
      <div className="filter-row">
        <Filter className="muted" aria-hidden />
        <label className="filter-field">
          <span>{t('filter.from')}</span>
          <input className="input" type="date" value={filter.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} />
        </label>
        <label className="filter-field">
          <span>{t('filter.to')}</span>
          <input className="input" type="date" value={filter.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} />
        </label>
        <label className="filter-field">
          <span>{t('resp.col_source')}</span>
          <select className="select" value={filter.source ?? ''} onChange={(e) => set({ source: (e.target.value || undefined) as ResponseFilter['source'] })}>
            <option value="">{t('filter.any')}</option>
            <option value="web">{t('resp.web')}</option>
            <option value="mobile">{t('resp.mobile')}</option>
            {testTotal > 0 && <option value="test">{t('resp.test')}</option>}
          </select>
        </label>
        <label className="filter-field">
          <span>{t('approval.status')}</span>
          <select className="select" value={filter.approval ?? ''} onChange={(e) => set({ approval: (e.target.value || undefined) as ResponseFilter['approval'] })}>
            <option value="">{t('filter.any')}</option>
            <option value="approved">{t('approval.approved')}</option>
            <option value="pending">{t('approval.pending')}</option>
            <option value="rejected">{t('approval.rejected')}</option>
          </select>
        </label>
        <button className={`btn sm ${open ? '' : 'ghost'}`} onClick={() => setOpen(!open)} aria-expanded={open}>
          <Plus /> {t('filter.by_answer')}
          {conditions.length > 0 && <span className="pill">{conditions.length}</span>}
        </button>
        <span className="grow" />
        {testTotal > 0 && <Switch checked={!!filter.includeTest} onChange={(v) => set({ includeTest: v || undefined })} label={t('filter.include_test', { count: testTotal })} />}
        {canWeight && (
          <Switch
            checked={!!filter.weight}
            onChange={(v) => set({ weight: v || undefined })}
            label={
              <span className="row gap-4">
                <Scale className="icon-sm" /> {t('filter.weighted')}
              </span>
            }
          />
        )}
        {active && (
          <button className="btn ghost sm" onClick={() => onChange({ includeTest: filter.includeTest, weight: filter.weight })}>
            <X /> {t('builder.clear_filters')}
          </button>
        )}
      </div>
      {open && (
        <div className="stack gap-8 filter-conditions">
          {conditions.length > 1 && (
            <div className="row gap-8 small">
              {t('filter.responses_where')}
              <select className="select" style={{ width: 'auto', height: 30 }} value={filter.match ?? 'all'} onChange={(e) => set({ match: e.target.value as 'all' | 'any' })} aria-label={t('logic.match')}>
                <option value="all">{t('logic.all')}</option>
                <option value="any">{t('logic.any')}</option>
              </select>
              {t('logic.of_these')}
            </div>
          )}
          {conditions.map((c, i) => (
            <ConditionRow
              key={i}
              def={def}
              sources={sources}
              condition={c}
              onChange={(next) => setConditions(conditions.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setConditions(conditions.filter((_, j) => j !== i))}
            />
          ))}
          <div>
            <button
              className="btn sm"
              disabled={!sources.length}
              onClick={() => {
                const src = sources[0]!;
                setConditions([...conditions, { questionId: src.id, operator: operatorsFor(src)[0]!, value: defaultValue(src) }]);
              }}
            >
              <Plus /> {t('logic.add')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
